/**
 * useAutoSave Hook
 * 提供自动保存功能，支持防抖和定时保存
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  saveDraft,
  getDraft,
  deleteDraft,
  hasChanges,
  formatSavedTime,
  generateDraftId,
  DRAFT_SETTINGS,
} from '../services/draftService';

/**
 * 自动保存状态
 */
export const AutoSaveStatus = {
  IDLE: 'idle',           // 空闲
  SAVING: 'saving',       // 保存中
  SAVED: 'saved',         // 已保存
  ERROR: 'error',         // 保存失败
  CHANGED: 'changed',     // 有未保存的更改
};

/**
 * useAutoSave Hook
 * @param {Object} options 配置选项
 * @param {boolean} options.enabled - 是否启用自动保存
 * @param {number} options.interval - 定时保存间隔(毫秒)
 * @param {number} options.debounceDelay - 防抖延迟(毫秒)
 * @param {string} options.draftId - 草稿ID
 * @param {Function} options.onSave - 保存成功回调
 * @param {Function} options.onError - 保存失败回调
 * @param {Function} options.onRestore - 恢复草稿回调
 */
const useAutoSave = ({
  enabled = true,
  interval = DRAFT_SETTINGS.AUTO_SAVE_INTERVAL,
  debounceDelay = DRAFT_SETTINGS.DEBOUNCE_DELAY,
  draftId = null,
  onSave = null,
  onError = null,
  onRestore = null,
} = {}) => {
  // 状态
  const [status, setStatus] = useState(AutoSaveStatus.IDLE);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastSavedVersion, setLastSavedVersion] = useState(0);
  const [error, setError] = useState(null);

  // Refs
  const dataRef = useRef(null);           // 当前数据
  const savedDataRef = useRef(null);      // 上次保存的数据
  const debounceTimerRef = useRef(null);  // 防抖定时器
  const intervalTimerRef = useRef(null);  // 定时保存定时器
  const isMountedRef = useRef(true);      // 组件是否挂载

  /**
   * 执行保存
   */
  const performSave = useCallback(async () => {
    if (!enabled || !dataRef.current || !draftId) {
      return;
    }

    // 检查是否有变更
    if (!hasChanges(dataRef.current, savedDataRef.current)) {
      return;
    }

    setStatus(AutoSaveStatus.SAVING);
    setError(null);

    try {
      const result = saveDraft(draftId, {
        ...dataRef.current,
        _version: lastSavedVersion,
        _createdAt: savedDataRef.current?.meta?.createdAt,
      });

      if (!isMountedRef.current) return;

      if (result.success) {
        setStatus(AutoSaveStatus.SAVED);
        setLastSavedAt(result.savedAt);
        setLastSavedVersion(result.version);

        // 更新已保存的数据引用
        savedDataRef.current = getDraft(draftId);

        onSave?.(result);
      } else {
        setStatus(AutoSaveStatus.ERROR);
        setError(result.message);
        onError?.(result.message);
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      setStatus(AutoSaveStatus.ERROR);
      setError(err.message);
      onError?.(err.message);
    }
  }, [enabled, draftId, lastSavedVersion, onSave, onError]);

  /**
   * 触发保存（带防抖）
   */
  const triggerSave = useCallback(() => {
    if (!enabled) return;

    // 清除之前的防抖定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    setStatus(AutoSaveStatus.CHANGED);

    // 设置新的防抖定时器
    debounceTimerRef.current = setTimeout(() => {
      performSave();
    }, debounceDelay);
  }, [enabled, debounceDelay, performSave]);

  /**
   * 立即保存（不带防抖）
   */
  const saveNow = useCallback(() => {
    // 清除防抖定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    return performSave();
  }, [performSave]);

  /**
   * 更新数据
   * @param {Object} data - 新数据
   */
  const updateData = useCallback((data) => {
    dataRef.current = data;

    // 触发防抖保存
    triggerSave();
  }, [triggerSave]);

  /**
   * 恢复草稿
   * @returns {Object|null} 恢复的数据
   */
  const restoreDraft = useCallback(() => {
    if (!draftId) return null;

    const draft = getDraft(draftId);
    if (draft) {
      savedDataRef.current = draft;
      setLastSavedAt(draft.savedAt);
      setLastSavedVersion(draft.version);
      setStatus(AutoSaveStatus.SAVED);

      onRestore?.(draft);
      return draft.data;
    }

    return null;
  }, [draftId, onRestore]);

  /**
   * 清除草稿
   */
  const clearDraft = useCallback(() => {
    if (!draftId) return;

    deleteDraft(draftId);
    savedDataRef.current = null;
    setLastSavedAt(null);
    setLastSavedVersion(0);
    setStatus(AutoSaveStatus.IDLE);
  }, [draftId]);

  /**
   * 检查是否有未保存的更改
   */
  const hasUnsavedChanges = useCallback(() => {
    return hasChanges(dataRef.current, savedDataRef.current);
  }, []);

  /**
   * 获取格式化的保存时间
   */
  const getFormattedSaveTime = useCallback(() => {
    return formatSavedTime(lastSavedAt);
  }, [lastSavedAt]);

  // 设置定时保存
  useEffect(() => {
    if (!enabled || !interval) return;

    intervalTimerRef.current = setInterval(() => {
      if (hasChanges(dataRef.current, savedDataRef.current)) {
        performSave();
      }
    }, interval);

    return () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
      }
    };
  }, [enabled, interval, performSave]);

  // 组件卸载时保存
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      // 清除定时器
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
      }

      // 尝试保存未保存的更改
      if (dataRef.current && hasChanges(dataRef.current, savedDataRef.current)) {
        saveDraft(draftId, {
          ...dataRef.current,
          _version: lastSavedVersion,
          _createdAt: savedDataRef.current?.meta?.createdAt,
        });
      }
    };
  }, [draftId, lastSavedVersion]);

  // 页面关闭前保存
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges(dataRef.current, savedDataRef.current)) {
        // 同步保存
        saveDraft(draftId, {
          ...dataRef.current,
          _version: lastSavedVersion,
          _createdAt: savedDataRef.current?.meta?.createdAt,
        });

        // 显示离开提示
        e.preventDefault();
        e.returnValue = '您有未保存的更改，确定要离开吗？';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [draftId, lastSavedVersion]);

  return {
    // 状态
    status,
    lastSavedAt,
    lastSavedVersion,
    error,
    isSaving: status === AutoSaveStatus.SAVING,
    isSaved: status === AutoSaveStatus.SAVED,
    hasChanges: status === AutoSaveStatus.CHANGED,

    // 方法
    updateData,
    saveNow,
    restoreDraft,
    clearDraft,
    hasUnsavedChanges,
    getFormattedSaveTime,
  };
};

export default useAutoSave;
